import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSession } from '../lib/session';
import { updateOwnProfile } from '../lib/queries';
import { applyAccent, applyEffectiveAccent, readCachedAccent } from '../lib/accent';
import { ACCENT_PRESETS, type AccentPreset } from '../lib/accent-presets';
import { Halftone } from './Halftone';
import { useToast } from './Toast';
import './AccentBar.css';

/**
 * User-facing accent picker - the brand pack's sticky bar.
 *
 * Faithful port of the picker bar in
 * aios/brand/brand-pack-application.html: a slim full-width bar fixed to
 * the bottom of the viewport - halftone brand mark on the left, the
 * vetted v5 palette (family-grouped, divider-separated) in the middle,
 * current readout on the right. A pick recolours the whole app instantly
 * (one CSS custom property - see src/lib/accent.ts).
 *
 * Mounted globally (App.tsx), so it shows on the signed-out pages too:
 *  - Signed in  → persists to the member's profile (follows them across
 *    devices).
 *  - Signed out → no profile to write to, so it's a local/per-device
 *    accent (localStorage) until the user signs in.
 *
 * Hidden during impersonation (don't write the impersonated member's
 * prefs). This is the deliberate per-user model (migration 022).
 */

/** Group the (family-ordered) presets into consecutive same-family runs. */
function groupByFamily(presets: AccentPreset[]): AccentPreset[][] {
  const groups: AccentPreset[][] = [];
  for (const p of presets) {
    const last = groups[groups.length - 1];
    if (last && last[0].family === p.family) last.push(p);
    else groups.push([p]);
  }
  return groups;
}

const GROUPS = groupByFamily(ACCENT_PRESETS);

export function AccentBar() {
  const { profile, impersonation } = useSession();
  const toast = useToast();
  // Sign-in has a left carbon stripe + right paper form split. There the
  // bar is a compact, swatches-only cluster centred over the right
  // (form) side - see .accentbar--signin in AccentBar.css.
  const onSignIn = useLocation().pathname === '/sign-in';
  const [saving, setSaving] = useState(false);
  // Current pick (hex) or null. Signed in: the saved profile accent.
  // Signed out: whatever's currently applied on this device.
  const [selected, setSelected] = useState<string | null>(
    profile?.accent ?? readCachedAccent().accent,
  );

  // Re-seed on auth change (sign in/out) or a profile refetch.
  useEffect(() => {
    setSelected(profile ? profile.accent ?? null : readCachedAccent().accent);
  }, [profile?.id, profile?.accent]);

  // Keep the active swatch in sync with picks made elsewhere (e.g. the
  // onboarding accent grid), which broadcast a 'cc:accent-pick' event.
  useEffect(() => {
    const onPick = (e: Event) => {
      const hex = (e as CustomEvent<string | null>).detail ?? null;
      setSelected(hex);
    };
    window.addEventListener('cc:accent-pick', onPick as EventListener);
    return () => window.removeEventListener('cc:accent-pick', onPick as EventListener);
  }, []);

  // When a text field is focused the mobile keyboard opens and shrinks the
  // visual viewport - which made this fixed accent bar (and the contact pill +
  // sticky bid bar) detach and jumble over the page mid-scroll. Flag it on
  // <body> so those fixed elements hide while typing (CSS, phones only).
  useEffect(() => {
    const isTextEntry = (el: EventTarget | null): boolean => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.isContentEditable || el.tagName === 'TEXTAREA') return true;
      if (el.tagName !== 'INPUT') return false;
      const t = (el as HTMLInputElement).type;
      return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'file', 'color'].includes(t);
    };
    const onIn = (e: FocusEvent) => { if (isTextEntry(e.target)) document.body.classList.add('kb-open'); };
    const onOut = () => document.body.classList.remove('kb-open');
    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    return () => {
      document.removeEventListener('focusin', onIn);
      document.removeEventListener('focusout', onOut);
      document.body.classList.remove('kb-open');
    };
  }, []);

  // Don't expose the impersonated member's prefs while impersonating.
  if (impersonation.active) return null;

  // Apply a pick. Signed in → persist to the profile (optimistic, with
  // revert on failure). Signed out → local/per-device only, no network.
  async function persist(accent: string | null) {
    const prev = selected;
    setSelected(accent);
    // Instant repaint. null → fall back to the admin global default.
    if (accent) applyAccent({ accent, accent_strong: accent });
    else applyEffectiveAccent(null);
    // Tell any other accent UI (the onboarding grid) to match.
    window.dispatchEvent(new CustomEvent('cc:accent-pick', { detail: accent }));

    if (!profile) return; // signed out - localStorage is the only store

    setSaving(true);
    try {
      await updateOwnProfile(profile.id, { accent, accent_strong: accent });
    } catch (err: any) {
      setSelected(prev);
      if (prev) applyAccent({ accent: prev, accent_strong: prev });
      else applyEffectiveAccent(null);
      toast.error(err?.message ?? "Couldn't save your accent.");
    } finally {
      setSaving(false);
    }
  }

  const active = selected
    ? ACCENT_PRESETS.find((p) => p.hex.toLowerCase() === selected.toLowerCase())
    : undefined;

  return (
    <div
      className={`accentbar ${onSignIn ? 'accentbar--signin' : ''}`}
      role="region"
      aria-label="Choose your accent colour"
    >
      <div className="accentbar__mark" aria-hidden="true">
        <Halftone type="edge-bloom" w={96} h={56} />
      </div>
      <div className="accentbar__eyebrow">Your accent</div>

      <div className="accentbar__swatches">
        {GROUPS.flatMap((group, gi) => [
          gi > 0 && (
            <span
              key={`div-${group[0].family}`}
              className="accentbar__divider"
              aria-hidden="true"
            />
          ),
          ...group.map((p) => {
            const on = selected?.toLowerCase() === p.hex.toLowerCase();
            return (
              <button
                key={p.name}
                type="button"
                className={`accentbar__swatch ${on ? 'is-active' : ''}`}
                style={{ background: p.hex }}
                onClick={() => persist(p.hex)}
                disabled={saving}
                title={`${p.family} · ${p.tone} · ${p.hex}`}
                aria-label={`${p.family} ${p.tone}`}
                aria-pressed={on}
              />
            );
          }),
        ])}
      </div>

      <div className="accentbar__current" aria-live="polite">
        {active ? (
          <strong>{`${active.family} · ${active.tone}`}</strong>
        ) : (
          'Pure B&W'
        )}
      </div>
    </div>
  );
}
